import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import debounce from "lodash/debounce";
import { HiOutlineInformationCircle } from "react-icons/hi";
import { FaPlus, FaFolder, FaFileLines } from "react-icons/fa6";
import { IoSearch, IoClose } from "react-icons/io5";
import AddOrgModal from "./AddOrgModal";
import NewFolderModal from "./NewFolderModal";
import MoveUploadModal from "./MoveUploadModal";
import RenameUploadModal from "./RenameUploadModal";
import PreviewUploadModal from "./PreviewUploadModal";
import TrashBinModal from "./TrashBinModal";
import ManageMembersModal from "./ManageMembersModal";
import OrgListPanel from "./OrgListPanel";
import FolderTreePanel from "./FolderTreePanel";
import FileDetailPanel from "./FileDetailPanel";
import {
  useGetAllOrgs,
  useGetFolderTree,
  useAddUpdateOrg,
  useAddUpdateFolder,
  useDeleteOrg,
  useGetUploadsByParent,
  useMoveUpload,
  useCopyUpload,
  useUploadFiles,
  useRenameUpload,
  useDeleteUpload,
  useUpdateUploadLock,
  useUpdateUploadStatus,
  useUpdateUploadTags,
  useDeleteForeverOrg,
  useGetAssignedUsers,
  useGetUsersList,
  useMutateAssignedUser,
  useGetTrashData,
  useDeleteForeverUpload,
  fetchSearchDetails,
} from "./dnsService";
import sweetAlert from "../Common/sweetAlert";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { getMenuPermission } from "../../Redux/SideBarSlice";
import {
  isInternalItemDrag,
  parseDragMoveItem,
  setDragMoveItem,
} from "./dnsDragMove";

/* ─── Main Component ─────────────────────────────────── */
const OrganizationsDNS = ({ isVisible, setSidebarOpen, sidebarOpen, isDark, setIsDark }) => {
  const dispatch = useDispatch();
  const { token, user } = useSelector((state) => state.root.auth);
  const { data } = useSelector((state) => state.root.Sidebar);

  // ── Shared / Cross-panel state ───────────────────────────────
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [isSorted, setIsSorted] = useState(false); // shared by tree + file panel
  const [expandedFolderIds, setExpandedFolderIds] = useState(new Set());
  const [pendingNavigation, setPendingNavigation] = useState(null); // { folderId, orgId }

  // Global search (header bar)
  const [globalSearchQuery, setGlobalSearchQuery] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchRef = useRef(null);
  const searchAbortRef = useRef(null);

  // Drag-move (position tracked in parent so both tree & table can react)
  const [draggingMoveItem, setDraggingMoveItem] = useState(null);
  const [moveDropTargetId, setMoveDropTargetId] = useState(null);

  // Modal states
  const [showAddOrg, setShowAddOrg] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [isCopy, setIsCopy] = useState(false);
  const [movingItem, setMovingItem] = useState(null);
  const [targetParentId, setTargetParentId] = useState("");
  const [renameModal, setRenameModal] = useState(null); // { id, name, type }
  const [renameValue, setRenameValue] = useState("");
  const [previewFile, setPreviewFile] = useState(null);
  const [tagsModal, setTagsModal] = useState(null); // { id, name, tags }
  const [tagsValue, setTagsValue] = useState("");
  const [showManageMembers, setShowManageMembers] = useState(false);
  const [memberOrg, setMemberOrg] = useState(null);
  const [memberFolder, setMemberFolder] = useState(null);
  const [showTrashModal, setShowTrashModal] = useState(false);
  const [trashActiveTab, setTrashActiveTab] = useState("orgs");

  // Module permissions
  const [permissions, setPermissions] = useState({
    isView: false,
    isSave: false,
    isDelete: false,
  });

  // ── Module permissions ────────────────────────────────────────
  useEffect(() => {
    const loadPermissions = async () => {
      if (!user?.userRoleID) return;
      const sidebarData = data?.bottummenu ?? [];
      const moduleItem = sidebarData?.find(
        (item) => item?.pageName === "Organizations"
      );
      const permissionRes = await dispatch(
        getMenuPermission({
          UserRoleID: user.userRoleID,
          ModuleID: moduleItem?.moduleID ?? 0,
        })
      );
      const accessList = permissionRes?.payload?.data;
      if (Array.isArray(accessList) && accessList.length > 0) {
        setPermissions({
          isView: !!accessList[0].isView,
          isSave: !!accessList[0].isSave,
          isDelete: !!accessList[0].isDelete,
        });
      }
    };
    loadPermissions();
  }, [dispatch, user?.userRoleID, data]);

  // ── React Query — data ────────────────────────────────────────
  const { data: orgs = [], isLoading: orgsLoading } = useGetAllOrgs(token);

  const activeOrgs = useMemo(() => {
    if (!Array.isArray(orgs)) return [];
    return orgs.filter((o) => !o.isDeleted);
  }, [orgs, user]);

  const deletedOrgs = useMemo(
    () => (Array.isArray(orgs) ? orgs.filter((o) => o.isDeleted) : []),
    [orgs]
  );

  const {
    data: treeResponse = { model: [], organizations: [] },
    isLoading: treeLoading,
  } = useGetFolderTree(token, selectedOrg?.organizationsDNSId);

  const folderTree = selectedOrg ? treeResponse?.model ?? [] : [];
  const orgStorageDetails = selectedOrg
    ? treeResponse?.organizations?.[0] ?? null
    : null;

  // ── Permissions derived from org assignment ───────────────────
  const effectivePermissions = useMemo(() => {
    const assigned = orgStorageDetails?.assigned;
    if (assigned && typeof assigned === "object") {
      return {
        isView: !!assigned.isView,
        isEdit: !!assigned.isEdit,
        isDelete: !!assigned.isDelete,
        isSave: !!assigned.isEdit,
      };
    }
    return {
      isView: !!permissions.isView,
      isEdit: !!permissions.isSave,
      isDelete: !!permissions.isDelete,
      isSave: !!permissions.isSave,
    };
  }, [orgStorageDetails?.assigned, permissions]);

  // Find root folder ancestor for a given folder id
  const findRootFolderForNode = (folderId, tree) => {
    for (const rootNode of tree) {
      const rootId = rootNode.folderDNSID ?? 0;
      if (rootId === folderId) return rootNode;
      const findInChildren = (node, targetId) => {
        if (!node.children) return false;
        for (const child of node.children) {
          const cId = child.folderDNSID ?? child.folderID;
          if (cId === targetId) return true;
          if (findInChildren(child, targetId)) return true;
        }
        return false;
      };
      if (findInChildren(rootNode, folderId)) return rootNode;
    }
    return null;
  };

  const folderPermissions = useMemo(() => {
    const folderId = selectedFolder?.folderDNSID ?? 0;
    if (!folderId || !folderTree.length) return effectivePermissions;
    if (user?.parentID === 0) return effectivePermissions;
    const rootFolder = findRootFolderForNode(folderId, folderTree);
    if (!rootFolder || !rootFolder.assignedUsers?.length)
      return effectivePermissions;
    const userAssignment = rootFolder.assignedUsers[0];
    if (!userAssignment) {
      return { isView: false, isEdit: false, isDelete: false, isSave: false };
    }
    return {
      isView: !!userAssignment?.isView,
      isEdit: !!userAssignment?.isEdit,
      isDelete: !!userAssignment?.isDelete,
      isSave: !!userAssignment?.isEdit,
    };
  }, [selectedFolder, folderTree, effectivePermissions, user]);

  // ── React Query — mutations ───────────────────────────────────
  const addUpdateOrgMutation = useAddUpdateOrg(token);
  const addUpdateFolderMutation = useAddUpdateFolder(
    token,
    selectedOrg?.organizationsDNSId
  );
  const deleteOrgMutation = useDeleteOrg(token);

  const isFolderSelected = !!(
    selectedFolder?.folderDNSID || selectedFolder?.folderID
  );
  const parentIdForUploads = isFolderSelected
    ? selectedFolder?.folderDNSID ?? selectedFolder?.folderID
    : undefined;

  const {
    data: dynamicUploads = [],
    isLoading: uploadsLoading,
    isFetching: uploadsFetching,
  } = useGetUploadsByParent(
    token,
    selectedOrg?.organizationsDNSId,
    parentIdForUploads
  );

  const moveUploadMutation = useMoveUpload(token, selectedOrg?.organizationsDNSId);
  const copyUploadMutation = useCopyUpload(token, selectedOrg?.organizationsDNSId);
  const uploadFilesMutation = useUploadFiles(token, selectedOrg?.organizationsDNSId);
  const renameUploadMutation = useRenameUpload(token, selectedOrg?.organizationsDNSId);
  const deleteUploadMutation = useDeleteUpload(token, selectedOrg?.organizationsDNSId);
  const updateUploadLockMutation = useUpdateUploadLock(token, selectedOrg?.organizationsDNSId);
  const updateUploadStatusMutation = useUpdateUploadStatus(token, selectedOrg?.organizationsDNSId);
  const updateUploadTagsMutation = useUpdateUploadTags(token, selectedOrg?.organizationsDNSId);
  const deleteForeverOrgMutation = useDeleteForeverOrg(token);
  const { data: trashData = [], isLoading: trashLoading } = useGetTrashData(
    token,
    selectedOrg?.organizationsDNSId
  );
  const deleteForeverUploadMutation = useDeleteForeverUpload(
    token,
    selectedOrg?.organizationsDNSId
  );
  const { data: assignedUsers = [] } = useGetAssignedUsers(
    token,
    memberOrg?.organizationsDNSId,
    memberFolder?.folderDNSID ?? memberFolder?.folderID ?? null
  );
  const { data: allUsers = [] } = useGetUsersList(token, user?.userID);
  const mutateAssignedUserMutation = useMutateAssignedUser(
    token,
    memberOrg?.organizationsDNSId,
    memberFolder?.folderDNSID ?? memberFolder?.folderID ?? null
  );

  const activeFolderIdForMembers =
    memberFolder?.folderDNSID ?? memberFolder?.folderID ?? null;
  const visibleAssignedUsers = useMemo(() => {
    if (!Array.isArray(assignedUsers)) return [];
    if (activeFolderIdForMembers) {
      return assignedUsers.filter(
        (u) => Number(u?.folderDNSID) === Number(activeFolderIdForMembers)
      );
    }
    return assignedUsers.filter(
      (u) => !u?.folderDNSID || Number(u.folderDNSID) === 0
    );
  }, [assignedUsers, activeFolderIdForMembers]);

  // ── Sync selection to active orgs ────────────────────────────
  useEffect(() => {
    if (activeOrgs.length === 0) {
      if (selectedOrg) {
        setSelectedOrg(null);
        setSelectedFolder(null);
        setBreadcrumb([]);
      }
      return;
    }
    if (!selectedOrg) {
      setSelectedOrg(activeOrgs[0]);
      setBreadcrumb([{ name: activeOrgs[0].organizationsName, node: null }]);
      return;
    }
    const stillActive = activeOrgs.some(
      (o) => o.organizationsDNSId === selectedOrg.organizationsDNSId
    );
    if (!stillActive) {
      setSelectedOrg(activeOrgs[0]);
      setBreadcrumb([{ name: activeOrgs[0].organizationsName, node: null }]);
    }
  }, [activeOrgs]);

  // ── Reset folder when org changes ────────────────────────────
  useEffect(() => {
    setSelectedFolder(null);
    setExpandedFolderIds(new Set());
    if (selectedOrg)
      setBreadcrumb([{ name: selectedOrg.organizationsName, node: null }]);
  }, [selectedOrg?.organizationsDNSId]);

  // ── Listen for openMoveModal events from TreeNode ─────────────
  useEffect(() => {
    const handleOpen = (e) => {
      setMovingItem(e.detail);
      setIsCopy(!!e.detail.isCopy);
      setTargetParentId("");
      setShowMoveModal(true);
    };
    window.addEventListener("openMoveModal", handleOpen);
    return () => window.removeEventListener("openMoveModal", handleOpen);
  }, []);

  // ── Cross-org search redirect once tree loads ─────────────────
  useEffect(() => {
    if (!pendingNavigation || treeLoading || !folderTree.length) return;
    if (
      String(selectedOrg?.organizationsDNSId) !==
      String(pendingNavigation.orgId)
    )
      return;

    const targetId = pendingNavigation.folderId;
    const findFolder = (nodes, targetId) => {
      for (const n of nodes) {
        const nId = n.folderDNSID ?? n.folderID ?? n.id;
        if (String(nId) === String(targetId)) return n;
        if (n.children?.length) {
          const found = findFolder(n.children, targetId);
          if (found) return found;
        }
      }
      return null;
    };
    const expandAncestors = (nodes, targetId, path = []) => {
      for (const n of nodes) {
        const nId = n.folderDNSID ?? n.folderID ?? n.id;
        if (String(nId) === String(targetId)) return [...path, nId];
        if (n.children?.length) {
          const result = expandAncestors(n.children, targetId, [...path, nId]);
          if (result) return result;
        }
      }
      return null;
    };

    const folder = findFolder(folderTree, targetId);
    if (folder) {
      const ancestorPath = expandAncestors(folderTree, targetId) || [];
      setExpandedFolderIds((prev) => {
        const next = new Set(prev);
        ancestorPath.forEach((id) => next.add(id));
        return next;
      });
      handleSelectFolder(folder);
    } else {
      handleSelectOrg(selectedOrg);
    }
    setPendingNavigation(null);
  }, [folderTree, pendingNavigation, selectedOrg, treeLoading]);

  // ── Breadcrumb builder ────────────────────────────────────────
  const buildBreadcrumbPath = useCallback(
    (targetFolderId) => {
      const orgCrumb = {
        name: selectedOrg?.organizationsName || "Organization",
        node: null,
      };
      if (!targetFolderId) return [orgCrumb];
      const findPath = (nodes, targetId, path = []) => {
        for (const n of nodes) {
          const nId = n.folderDNSID ?? n.folderID ?? n.id;
          const current = { name: n.folderName ?? n.name, node: n };
          if (nId === targetId) return [...path, current];
          if (n.children?.length) {
            const result = findPath(n.children, targetId, [...path, current]);
            if (result) return result;
          }
        }
        return null;
      };
      const folderPath = findPath(folderTree, targetFolderId) || [];
      return [orgCrumb, ...folderPath];
    },
    [selectedOrg, folderTree]
  );

  // ── Stable callbacks ──────────────────────────────────────────
  const handleSelectOrg = useCallback(
    (org) => {
      setSelectedOrg(org);
      setBreadcrumb([{ name: org?.organizationsName, node: null }]);
      setSelectedFolder(org);
    },
    []
  );

  const handleSelectFolder = useCallback(
    (node) => {
      setSelectedFolder(node);
      const folderId = node.folderDNSID ?? node.folderID ?? node.id;
      setBreadcrumb(buildBreadcrumbPath(folderId));
    },
    [buildBreadcrumbPath]
  );

  // ── Global search effects & handlers ──────────────────────────
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const debouncedSearchApi = useMemo(
    () =>
      debounce(async (query, orgId) => {
        if (searchAbortRef.current) searchAbortRef.current.abort();
        const controller = new AbortController();
        searchAbortRef.current = controller;
        try {
          const result = await fetchSearchDetails(
            token,
            orgId,
            query.trim()
          );
          if (!controller.signal.aborted) {
            const items =
              result?.model ?? result?.data ?? result ?? [];
            setSearchSuggestions(Array.isArray(items) ? items : []);
            setSearchLoading(false);
          }
        } catch {
          if (!controller.signal.aborted) {
            setSearchSuggestions([]);
            setSearchLoading(false);
          }
        }
      }, 500),
    [token]
  );

  useEffect(() => {
    return () => {
      debouncedSearchApi.cancel();
      if (searchAbortRef.current) searchAbortRef.current.abort();
    };
  }, [debouncedSearchApi]);

  const handleGlobalSearch = (value) => {
    setGlobalSearchQuery(value);
    if (!value.trim() || !selectedOrg) {
      debouncedSearchApi.cancel();
      setSearchSuggestions([]);
      setShowSearchDropdown(false);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    setShowSearchDropdown(true);
    debouncedSearchApi(value, selectedOrg.organizationsDNSId);
  };

  const findFolderInTree = useCallback((nodes, targetId) => {
    for (const n of nodes) {
      const nId = n.folderDNSID ?? n.folderID ?? n.id;
      if (nId === targetId) return n;
      if (n.children?.length) {
        const found = findFolderInTree(n.children, targetId);
        if (found) return found;
      }
    }
    return null;
  }, []);

  const handleSearchSuggestionClick = useCallback(
    (item) => {
      setGlobalSearchQuery("");
      setShowSearchDropdown(false);
      setSearchSuggestions([]);

      const targetOrgId =
        item.organizationsDNSId ?? item.organizationsDnsId;
      const isFolder =
        (item.fileType || item.type || "").toLowerCase() === "folder" ||
        (!item.parentID && !item.parentId);
      const targetFolderId = isFolder
        ? item.uploadID ?? item.folderDNSID ?? item.id
        : item.parentID ?? item.parentId ?? 0;

      // Cross-org navigation
      if (
        targetOrgId &&
        String(targetOrgId) !== String(selectedOrg?.organizationsDNSId)
      ) {
        const targetOrg = activeOrgs.find(
          (o) => String(o.organizationsDNSId) === String(targetOrgId)
        );
        if (targetOrg) {
          setPendingNavigation({ folderId: targetFolderId, orgId: targetOrgId });
          handleSelectOrg(targetOrg);
          return;
        }
      }

      // Same org navigation
      const folder = findFolderInTree(folderTree, targetFolderId);
      if (folder) {
        handleSelectFolder(folder);
      } else {
        handleSelectOrg(selectedOrg);
      }
    },
    [
      selectedOrg,
      activeOrgs,
      folderTree,
      findFolderInTree,
      handleSelectOrg,
      handleSelectFolder,
    ]
  );

  // ── Folder tree helpers ───────────────────────────────────────
  const sortTreeNodes = (nodes) => {
    if (!nodes) return [];
    return [...nodes]
      .sort((a, b) => {
        const nameA = (a.folderName ?? a.name ?? "").toLowerCase();
        const nameB = (b.folderName ?? b.name ?? "").toLowerCase();
        return nameA.localeCompare(nameB);
      })
      .map((n) => ({
        ...n,
        children: n.children ? sortTreeNodes(n.children) : [],
      }));
  };
  const renderedTree = isSorted ? sortTreeNodes(folderTree) : folderTree;

  // O(1) folder lookup used in file table item-count badges
  const qc = useQueryClient();
  const folderNodeMap = useMemo(() => {
    const map = new Map();
    const traverse = (nodes) => {
      nodes.forEach((n) => {
        const id = n.folderDNSID ?? n.folderID ?? n.id;
        map.set(id, n);
        if (n.children?.length) traverse(n.children);
      });
    };
    traverse(folderTree);
    return map;
  }, [folderTree]);

  const uploadsCountMap = useMemo(() => {
    const map = new Map();
    const allCached = qc.getQueriesData({ queryKey: ["dns", "uploads"] });
    allCached.forEach(([queryKey, data]) => {
      if (Array.isArray(data) && data.length > 0) {
        const parentId = queryKey[3];
        if (parentId !== undefined && parentId !== null) {
          map.set(parentId, data.length);
        }
      }
    });
    if (parentIdForUploads !== undefined && parentIdForUploads !== null) {
      map.set(parentIdForUploads, dynamicUploads.length);
    }
    return map;
  }, [qc, dynamicUploads, parentIdForUploads]);

  // Drag-move ancestry check
  const isFolderDescendantOf = (ancestorId, candidateId) => {
    if (ancestorId == null || candidateId == null) return false;
    if (Number(ancestorId) === Number(candidateId)) return true;
    const node = folderNodeMap.get(Number(ancestorId));
    if (!node?.children?.length) return false;
    const stack = [...node.children];
    while (stack.length) {
      const n = stack.pop();
      const id = n.folderDNSID ?? n.folderID ?? n.id;
      if (Number(id) === Number(candidateId)) return true;
      if (n.children?.length) stack.push(...n.children);
    }
    return false;
  };

  const canDropMoveItemOnFolder = (targetFolderId, item) => {
    if (!item || !folderPermissions.isEdit) return false;
    const target = targetFolderId ?? 0;
    if (item.type === "Folder") {
      if (Number(item.id) === Number(target)) return false;
      if (target !== 0 && isFolderDescendantOf(item.id, target)) return false;
    }
    return true;
  };



  const toggleFolderExpanded = useCallback((folderId) => {
    setExpandedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  }, []);

  const handleExpandAll = useCallback(() => {
    const allIds = new Set();
    const traverse = (nodes) => {
      nodes.forEach((n) => {
        const id = n.folderDNSID ?? n.folderID ?? n.id;
        allIds.add(id);
        if (n.children?.length > 0) traverse(n.children);
      });
    };
    traverse(folderTree);
    setExpandedFolderIds(allIds);
  }, [folderTree]);

  const handleCollapseAll = useCallback(() => {
    setExpandedFolderIds(new Set());
  }, []);

  // ── Org CRUD callbacks ────────────────────────────────────────
  const handleAddOrg = useCallback(
    async (form) => {
      const payload = {
        organizationsDNSId: editingOrg ? editingOrg.organizationsDNSId : 0,
        organizationsName: form.name,
        organizationsCode: form.code,
        description: form.desc,
        storageLimit: parseInt(form.storage) || 50,
        isActive: form.status,
      };
      try {
        await addUpdateOrgMutation.mutateAsync(payload);
        setShowAddOrg(false);
        setEditingOrg(null);
        toast.success(
          editingOrg
            ? "OrganizationsDNS Updated Successfully!"
            : "OrganizationsDNS Saved Successfully!"
        );
      } catch {
        toast.error(
          editingOrg
            ? "Failed to update organization."
            : "Failed to add organization."
        );
      }
    },
    [addUpdateOrgMutation, editingOrg]
  );

  const handleOpenAddOrg = useCallback(() => setShowAddOrg(true), []);

  const handleEditOrg = useCallback((org) => {
    setEditingOrg(org);
    setShowAddOrg(true);
  }, []);

  const handleDeleteOrg = useCallback(
    (org) => {
      const id = org.organizationsDNSId;
      const orgName = org.organizationsName || "Organization";
      sweetAlert
        .confirm(
          "Delete Organization?",
          `This moves ${orgName} and all its folders/files to Trash. You can restore it within the retention window.`,
          "delete"
        )
        .then(async (result) => {
          if (result.isConfirmed) {
            try {
              await deleteOrgMutation.mutateAsync({ id, isDeleted: true });
              toast.success(`"${orgName}" moved to Trash successfully!`);
              if (selectedOrg?.organizationsDNSId === id) setSelectedOrg(null);
            } catch {
              toast.error("Failed to delete organization.");
            }
          }
        });
    },
    [deleteOrgMutation, selectedOrg]
  );

  const handleManageMembersOrg = useCallback((org) => {
    setMemberOrg(org);
    setShowManageMembers(true);
  }, []);

  // ── Folder callbacks ──────────────────────────────────────────
  const handleOpenNewFolder = useCallback(() => setShowNewFolder(true), []);

  const handleAddFolder = useCallback(
    async (form) => {
      if (!selectedOrg) return;
      const payload = {
        folderDNSID: 0,
        folderName: form.name,
        fileSize: "0B",
        parentId: selectedFolder?.folderDNSID ?? 0,
        userID: user?.userID ?? 0,
        organizationsDNSId: selectedOrg.organizationsDNSId,
        operation: "Insert",
        folderIconId: form.icon + 1,
        description: form.desc,
      };
      try {
        await addUpdateFolderMutation.mutateAsync(payload);
        toast.success("Folder created!");
      } catch {
        toast.error("Failed to create folder.");
      }
    },
    [addUpdateFolderMutation, selectedOrg, selectedFolder, user]
  );

  // ── Upload rename callback (shared by tree + file table) ──────
  const openRenameModal = useCallback((id, name, type) => {
    setRenameModal({ id, name, type });
    setRenameValue(name);
  }, []);

  const handleRenameConfirm = async () => {
    if (!renameModal || !renameValue.trim()) return;
    try {
      await renameUploadMutation.mutateAsync({
        type: renameModal.type,
        uploadId: renameModal.id,
        fileName: renameValue.trim(),
      });
      toast.success(`Renamed to "${renameValue.trim()}" successfully!`);
      setRenameModal(null);
    } catch {
      toast.error("Rename failed. Please try again.");
    }
  };

  // ── Tags callback ─────────────────────────────────────────────
  const handleOpenTags = useCallback((file) => {
    setTagsModal({ id: file.uploadID, name: file.fileName, tags: file.tags || "" });
    setTagsValue(file.tags || "");
  }, []);

  const handleUpdateTags = async () => {
    if (!tagsModal) return;
    try {
      await updateUploadTagsMutation.mutateAsync({
        uploadId: tagsModal.id,
        tags: tagsValue.trim(),
      });
      toast.success("Tags updated successfully!");
      setTagsModal(null);
    } catch {
      toast.error("Failed to update tags.");
    }
  };

  // ── Preview callback ──────────────────────────────────────────
  const handlePreviewFile = useCallback((file) => setPreviewFile(file), []);

  // ── Move / copy callbacks ─────────────────────────────────────
  const handleStartMove = useCallback((item, copy) => {
    setMovingItem(item);
    setIsCopy(copy);
    setTargetParentId("");
    setShowMoveModal(true);
  }, []);

  const handleMoveConfirm = async () => {
    if (!movingItem || !targetParentId) return;
    try {
      if (isCopy) {
        await copyUploadMutation.mutateAsync({
          type: movingItem.type,
          uploadId: movingItem.id,
          newParentId: parseInt(targetParentId),
        });
        toast.success(`${movingItem.type} Copied Successfully!`);
      } else {
        await moveUploadMutation.mutateAsync({
          type: movingItem.type,
          uploadId: movingItem.id,
          newParentId: parseInt(targetParentId),
        });
        toast.success(`${movingItem.type} Moved Successfully!`);
      }
      setShowMoveModal(false);
      setMovingItem(null);
    } catch {
      toast.error(
        `Failed to ${isCopy ? "copy" : "move"} ${movingItem.type.toLowerCase()}.`
      );
    }
  };

  // ── Trash callbacks ───────────────────────────────────────────
  const handleOpenTrash = useCallback(() => setShowTrashModal(true), []);

  const handleRestoreOrg = async (id, orgName) => {
    try {
      await deleteOrgMutation.mutateAsync({ id, isDeleted: false });
      toast.success(`"${orgName}" restored successfully!`);
    } catch {
      toast.error("Failed to restore organization.");
    }
  };

  const handleDeleteOrgForever = async (id, orgName) => {
    sweetAlert
      .confirm(
        "Delete Forever?",
        `Are you sure you want to permanently delete "${orgName}"? This action cannot be undone.`,
        "delete"
      )
      .then(async (result) => {
        if (result.isConfirmed) {
          try {
            await deleteForeverOrgMutation.mutateAsync(id);
            toast.success(`"${orgName}" permanently deleted.`);
          } catch {
            toast.error("Failed to permanently delete organization.");
          }
        }
      });
  };

  const handleRestoreUpload = async (id, name, type) => {
    try {
      const payload = [{ Type: type, UploadIDs: String(id), isDeleted: false }];
      await deleteUploadMutation.mutateAsync({ payload });
      toast.success(`"${name}" restored successfully!`);
    } catch {
      toast.error("Failed to restore item.");
    }
  };

  const handleDeleteUploadForever = async (id, name, type) => {
    sweetAlert
      .confirm(
        "Delete Forever?",
        `Are you sure you want to permanently delete "${name}"? This action cannot be undone.`,
        "delete"
      )
      .then(async (result) => {
        if (result.isConfirmed) {
          try {
            const payload = [{ type, uploadIDs: String(id), isDeleted: true }];
            await deleteForeverUploadMutation.mutateAsync(payload);
            toast.success(`"${name}" permanently deleted.`);
          } catch {
            toast.error("Failed to permanently delete item.");
          }
        }
      });
  };

  // ── Single-folder delete (from tree) ─────────────────────────
  const handleDeleteFolderSingle = useCallback(
    (id, name, type) => {
      sweetAlert
        .confirm(
          `Delete ${type}?`,
          `Are you sure you want to delete "${name}"? This cannot be undone.`,
          "delete"
        )
        .then(async (result) => {
          if (result.isConfirmed) {
            try {
              const payload = [{ Type: type, UploadIDs: String(id), isDeleted: true }];
              await deleteUploadMutation.mutateAsync({ payload });
              toast.success(`"${name}" deleted successfully!`);
            } catch {
              toast.error(`Failed to delete ${type.toLowerCase()}.`);
            }
          }
        });
    },
    [deleteUploadMutation]
  );

  // ── Members callbacks ─────────────────────────────────────────
  const openFolderManageMembers = useCallback(
    (folderNode) => {
      if (!selectedOrg || !folderNode) return;
      setMemberOrg(selectedOrg);
      setMemberFolder(folderNode);
      setShowManageMembers(true);
    },
    [selectedOrg]
  );

  // onAddMember receives { selectedUserToAssign, memberPermissions, resetForm } from the modal
  const handleAddMember = async ({ selectedUserToAssign, memberPermissions, resetForm }) => {
    if (!selectedUserToAssign) {
      toast.error("Please select a user to assign.");
      return;
    }
    if (
      !memberPermissions.isView.checked &&
      !memberPermissions.isEdit.checked &&
      !memberPermissions.isDelete.checked
    ) {
      toast.error("Please select at least one permission.");
      return;
    }
    const folderIdForPayload =
      memberFolder?.folderDNSID ?? memberFolder?.folderID ?? null;
    const payload = {
      organizationsDNSId: memberOrg.organizationsDNSId,
      ...(folderIdForPayload ? { folderDNSID: folderIdForPayload } : {}),
      assignedUserId: parseInt(selectedUserToAssign),
      operation: "Insert",
      isView: !!memberPermissions.isView.checked,
      isEdit: !!memberPermissions.isEdit.checked,
      isDelete: !!memberPermissions.isDelete.checked,
    };
    try {
      await mutateAssignedUserMutation.mutateAsync(payload);
      toast.success("User assigned successfully!");
      resetForm?.();
    } catch {
      toast.error("Failed to assign user.");
    }
  };


  // Only clears parent-owned state; the modal resets its own local state via isOpen useEffect
  const resetMemberModal = () => {
    setShowManageMembers(false);
    setMemberOrg(null);
    setMemberFolder(null);
  };

  const handleRemoveMember = async (assignedId, userId) => {
    const payload = {
      organizationsDNSAssignedId: assignedId,
      assignedUserId: userId,
      operation: "Delete",
    };
    try {
      await mutateAssignedUserMutation.mutateAsync(payload);
      toast.success("User removed successfully!");
    } catch {
      toast.error("Failed to remove user.");
    }
  };

  // ── Drag-move handlers (shared by tree + file table) ──────────
  const handleMoveItemByDrag = async (item, newParentId) => {
    if (!item) return;
    const parentId = newParentId ?? 0;
    if (!canDropMoveItemOnFolder(parentId, item)) {
      toast.error("Can't move item into this folder.");
      return;
    }
    try {
      await moveUploadMutation.mutateAsync({
        type: item.type,
        uploadId: item.id,
        newParentId: parseInt(parentId, 10),
      });
      toast.success(`${item.type} moved successfully!`);
    } catch {
      toast.error("Move failed. Please try again.");
    }
  };

  const handleMoveDragStart = useCallback(
    (e, item) => {
      if (!folderPermissions.isEdit || moveUploadMutation.isPending) {
        e.preventDefault();
        return;
      }
      e.stopPropagation();
      setDragMoveItem(e.dataTransfer, item);
      setDraggingMoveItem(item);
    },
    [folderPermissions.isEdit, moveUploadMutation.isPending]
  );

  const handleMoveDragEnd = useCallback(() => {
    setDraggingMoveItem(null);
    setMoveDropTargetId(null);
  }, []);

  const handleMoveDragOverFolder = useCallback(
    (e, targetFolderId) => {
      if (!isInternalItemDrag(e.dataTransfer)) return;
      e.preventDefault();
      e.stopPropagation();
      const item = parseDragMoveItem(e.dataTransfer) || draggingMoveItem;
      if (!item || !canDropMoveItemOnFolder(targetFolderId, item)) {
        e.dataTransfer.dropEffect = "none";
        setMoveDropTargetId(null);
        return;
      }
      e.dataTransfer.dropEffect = "move";
      setMoveDropTargetId(targetFolderId ?? 0);
    },
    [draggingMoveItem, folderPermissions]
  );

  const handleMoveDropOnFolder = useCallback(
    async (e, targetFolderId) => {
      if (!isInternalItemDrag(e.dataTransfer)) return;
      e.preventDefault();
      e.stopPropagation();
      const item = parseDragMoveItem(e.dataTransfer) || draggingMoveItem;
      setDraggingMoveItem(null);
      setMoveDropTargetId(null);
      if (!item) return;
      await handleMoveItemByDrag(item, targetFolderId ?? 0);
    },
    [draggingMoveItem]
  );

  // ── Pending navigation callback (from FileDetailPanel search) ─
  const handlePendingNavigation = useCallback((nav) => {
    setPendingNavigation(nav);
  }, []);

  // ─────────────────────────────────────────────────────────────
  return (
    <>
      <div className="flex flex-1">
        <div className="page-wrapper relative ml-auto w-[calc(100%-326px)] px-4 pt-[54px] duration-300">
          {/* Page Header */}
          <div className="crm-page-header">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="crm-page-title">Organizations</h1>
                  <HiOutlineInformationCircle
                    size={16}
                    className="text-slate-400 mt-0.5"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-0.5 font-medium">
                  Manage and organize your data in one place.
                </p>
              </div>

              {/* Right side header controls: Search bar + Trash Bin + Add Organization */}
              <div className="flex items-center gap-3 flex-1 max-w-xl justify-end">
                {/* Global Search Bar */}
                <div className="relative flex-1 max-w-md" ref={searchRef}>
                  <div className="relative">
                    <IoSearch
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      type="text"
                      value={globalSearchQuery}
                      onChange={(e) => handleGlobalSearch(e.target.value)}
                      onFocus={() => {
                        if (searchSuggestions.length > 0)
                          setShowSearchDropdown(true);
                      }}
                      placeholder="Search documents, folders, users..."
                      className="w-full pl-9 pr-9 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 transition-all placeholder:text-slate-400 font-medium"
                    />
                    {globalSearchQuery && (
                      <button
                        onClick={() => {
                          setGlobalSearchQuery("");
                          setSearchSuggestions([]);
                          setShowSearchDropdown(false);
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        <IoClose size={16} />
                      </button>
                    )}
                  </div>

                  {/* Suggestions dropdown */}
                  {showSearchDropdown && (
                    <div
                      className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-xl shadow-2xl z-[100] max-h-[320px] overflow-y-auto"
                      style={{ boxShadow: "0 12px 40px -8px rgba(0,0,0,0.15)" }}
                    >
                      {searchLoading ? (
                        <div className="flex items-center justify-center gap-2 py-6">
                          <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                          <span className="text-xs text-slate-500 font-medium">
                            Searching...
                          </span>
                        </div>
                      ) : searchSuggestions.length === 0 ? (
                        <div className="py-6 text-center">
                          <p className="text-xs text-slate-400 font-medium">
                            No results found
                          </p>
                        </div>
                      ) : (
                        <div className="py-1.5">
                          {searchSuggestions.map((item, idx) => {
                            const isFolder =
                              (item.fileType || item.type || "").toLowerCase() ===
                              "folder";
                            return (
                              <button
                                key={item.uploadID ?? item.folderDNSID ?? idx}
                                onClick={() => handleSearchSuggestionClick(item)}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-indigo-50 transition-colors group"
                              >
                                <div
                                  className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isFolder
                                    ? "bg-amber-50 text-amber-500"
                                    : "bg-indigo-50 text-indigo-500"
                                    }`}
                                >
                                  {isFolder ? (
                                    <FaFolder size={14} />
                                  ) : (
                                    <FaFileLines size={14} />
                                  )}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-semibold text-slate-800 truncate group-hover:text-indigo-700 transition-colors">
                                    {item.fileName || item.folderName || "Untitled"}
                                  </p>
                                  <p className="text-sm font-normal text-slate-800 truncate group-hover:text-indigo-700 transition-colors">
                                    {item?.fullPath}
                                  </p>
                                  <p className="text-[11px] text-slate-400 font-medium mt-0.5 truncate">
                                    {item.fileType || "File"}{" "}
                                    {item.fileSize ? `· ${item.fileSize}` : ""}
                                  </p>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Trash Bin button */}
                {effectivePermissions.isDelete && (
                  <button
                    data-tooltip-id="toolbar-tooltip"
                    data-tooltip-content="Trash Bin"
                    onClick={handleOpenTrash}
                    className="flex items-center justify-center p-2 text-xs font-semibold bg-red text-white rounded-full transition-colors shrink-0"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
                      <path
                        fillRule="evenodd"
                        d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"
                      />
                    </svg>
                  </button>
                )}

                {/* Add Organization button */}
                {effectivePermissions.isEdit && (
                  <button
                    onClick={handleOpenAddOrg}
                    className="crm-new-btn shrink-0"
                  >
                    <FaPlus size={11} /> Add Organization
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* 3-Column Layout */}
          <div className="xl:w-full h-[calc(100vh-138px)] relative pb-14">
            <div className="flex gap-4 h-full">

              {/* ── Left Column ── */}
              <OrgListPanel
                activeOrgs={activeOrgs}
                selectedOrg={selectedOrg}
                onSelectOrg={handleSelectOrg}
                effectivePermissions={effectivePermissions}
                user={user}
                orgsLoading={orgsLoading}
                onAddOrg={handleOpenAddOrg}
                onEditOrg={handleEditOrg}
                onDeleteOrg={handleDeleteOrg}
                onManageMembers={handleManageMembersOrg}
              />

              {/* ── Middle Column ── */}
              <FolderTreePanel
                selectedOrg={selectedOrg}
                selectedFolder={selectedFolder}
                onSelectFolder={handleSelectFolder}
                renderedTree={renderedTree}
                treeLoading={treeLoading}
                expandedFolderIds={expandedFolderIds}
                toggleFolderExpanded={toggleFolderExpanded}
                handleExpandAll={handleExpandAll}
                handleCollapseAll={handleCollapseAll}
                isSorted={isSorted}
                setIsSorted={setIsSorted}
                folderPermissions={folderPermissions}
                user={user}
                draggingMoveItem={draggingMoveItem}
                moveDropTargetId={moveDropTargetId}
                onMoveDragStart={handleMoveDragStart}
                onMoveDragEnd={handleMoveDragEnd}
                onMoveDragOverFolder={handleMoveDragOverFolder}
                onMoveDropOnFolder={handleMoveDropOnFolder}
                uploadsCountMap={uploadsCountMap}
                onRenameFolder={openRenameModal}
                onDeleteFolder={handleDeleteFolderSingle}
                onManageMembers={openFolderManageMembers}
                orgStorageDetails={orgStorageDetails}
                onAddFolder={handleOpenNewFolder}
              />

              {/* ── Right Column ── */}
              <FileDetailPanel
                selectedOrg={selectedOrg}
                selectedFolder={selectedFolder}
                onSelectFolder={handleSelectFolder}
                onSelectOrg={handleSelectOrg}
                dynamicUploads={dynamicUploads}
                isUploadsLoading={uploadsLoading || uploadsFetching}
                folderPermissions={folderPermissions}
                user={user}
                breadcrumb={breadcrumb}
                isSorted={isSorted}
                folderNodeMap={folderNodeMap}
                uploadsCountMap={uploadsCountMap}
                deleteUploadMutation={deleteUploadMutation}
                updateUploadLockMutation={updateUploadLockMutation}
                updateUploadStatusMutation={updateUploadStatusMutation}
                updateUploadTagsMutation={updateUploadTagsMutation}
                uploadFilesMutation={uploadFilesMutation}
                onPreviewFile={handlePreviewFile}
                onOpenRename={openRenameModal}
                onOpenTags={handleOpenTags}
                onStartMove={handleStartMove}
                draggingMoveItem={draggingMoveItem}
                moveDropTargetId={moveDropTargetId}
                onMoveDragStart={handleMoveDragStart}
                onMoveDragEnd={handleMoveDragEnd}
                onMoveDragOverFolder={handleMoveDragOverFolder}
                onMoveDropOnFolder={handleMoveDropOnFolder}
                effectivePermissions={effectivePermissions}
                onTrashOpen={handleOpenTrash}
                onAddFolder={handleOpenNewFolder}
                token={token}
                orgs={orgs}
                folderTree={folderTree}
                onPendingNavigation={handlePendingNavigation}
              />

            </div>
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      {showAddOrg && (
        <AddOrgModal
          onClose={() => {
            setShowAddOrg(false);
            setEditingOrg(null);
          }}
          onSave={handleAddOrg}
          editingOrg={editingOrg}
          isPending={addUpdateOrgMutation.isPending}
        />
      )}
      {showNewFolder && (
        <NewFolderModal
          onClose={() => setShowNewFolder(false)}
          onSave={handleAddFolder}
          isPending={addUpdateFolderMutation.isPending}
        />
      )}

      <MoveUploadModal
        movingItem={movingItem}
        selectedOrg={selectedOrg}
        folderTree={folderTree}
        targetParentId={targetParentId}
        setTargetParentId={setTargetParentId}
        isCopy={isCopy}
        onClose={() => {
          setShowMoveModal(false);
          setMovingItem(null);
        }}
        onMove={handleMoveConfirm}
        isPending={moveUploadMutation.isPending || copyUploadMutation.isPending}
      />

      <RenameUploadModal
        renameModal={renameModal}
        renameValue={renameValue}
        setRenameValue={setRenameValue}
        onClose={() => setRenameModal(null)}
        onRename={handleRenameConfirm}
        isPending={renameUploadMutation.isPending}
      />

      <PreviewUploadModal
        previewFile={previewFile}
        onClose={() => setPreviewFile(null)}
      />

      {/* Tags reuses RenameUploadModal */}
      <RenameUploadModal
        renameModal={tagsModal}
        renameValue={tagsValue}
        setRenameValue={setTagsValue}
        onClose={() => setTagsModal(null)}
        onRename={handleUpdateTags}
        isPending={updateUploadTagsMutation.isPending}
        title={tagsModal ? `Add Tags — ${tagsModal.name}` : ""}
        label="Tags (comma separated)"
        placeholder="e.g. testing, finance, raw"
        saveLabel="Save"
      />

      <TrashBinModal
        isOpen={showTrashModal}
        onClose={() => setShowTrashModal(false)}
        selectedOrg={selectedOrg}
        activeTab={trashActiveTab}
        setActiveTab={setTrashActiveTab}
        deletedOrgs={deletedOrgs}
        trashData={trashData}
        trashLoading={trashLoading}
        deleteOrgMutation={deleteOrgMutation}
        deleteForeverOrgMutation={deleteForeverOrgMutation}
        deleteUploadMutation={deleteUploadMutation}
        deleteForeverUploadMutation={deleteForeverUploadMutation}
        onRestoreOrg={handleRestoreOrg}
        onDeleteOrgForever={handleDeleteOrgForever}
        onRestoreUpload={handleRestoreUpload}
        onDeleteUploadForever={handleDeleteUploadForever}
      />

      <ManageMembersModal
        isOpen={showManageMembers}
        memberOrg={memberOrg}
        memberFolder={memberFolder}
        visibleAssignedUsers={visibleAssignedUsers}
        allUsers={allUsers}
        mutateAssignedUserMutation={mutateAssignedUserMutation}
        onAddMember={handleAddMember}
        onRemoveMember={handleRemoveMember}
        onClose={resetMemberModal}
      />

      <Tooltip
        id="toolbar-tooltip"
        place="bottom"
        backgroundColor="#4338ca"
        type="warning"
        effect="solid"
        style={{
          zIndex: 9999,
          borderRadius: "8px",
          fontSize: "11px",
          fontWeight: 600,
          padding: "4px 10px",
          backgroundColor: "#4338ca",
        }}
      />
    </>
  );
};

export default OrganizationsDNS;
